/**
 * Expanded seed data for Watchmen Command — rich, realistic, high-volume.
 * All records conform to universal field schema.
 * tenantId: 'watchmen-demo' on all records.
 *
 * Volume targets:
 *   Customers: 25 | Carriers: 40 | Loads: 150 | Quotes: 60
 *   POs: 40 | Appointments: 50 | Carrier Vetting: 20 | IoT Trackers: 15
 *   IoT Events: 200 | Assets: 20 | Trucks: 12 | Financials: 80
 *   Events: 100 | Exceptions: 30
 *
 * IOT-DEMO-SEED-1: EXPANDED_IOT_TRACKERS and EXPANDED_ASSETS also include
 * kairos-demo fixtures (via re-export from iotDemoData.js) for Device Management.
 */
import {
  KAIROS_DEMO_DM_TRACKERS,
  KAIROS_DEMO_TRAILERS,
  KAIROS_DEMO_TRUCKS as _KD_TRUCKS,
} from './iotDemoData.js';

const T = 'watchmen-demo';
const pad = (n, len = 5) => String(n).padStart(len, '0');
const daysAgo = (d) => new Date(Date.now() - d * 86400000).toISOString();
const hoursAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randBetween = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
const randFloat = (a, b, dec = 1) => parseFloat((Math.random() * (b - a) + a).toFixed(dec));
const uid = (prefix, i) => `${prefix}-${pad(i)}`;

// ── Universal fields factory ──
function uf(extra = {}, daysBack = 0) {
  const created = daysAgo(daysBack);
  return { tenantId: T, customerId: extra.customerId ?? null, loadId: extra.loadId ?? null, quoteId: extra.quoteId ?? null, createdAt: created, createdBy: 'system', updatedAt: extra.updatedAt || created, updatedBy: 'system', deletedAt: null, deletedBy: null, isDeleted: false, version: 1, source: 'seed', ...extra };
}

// ═══════════════════════════════════════════════════════════════════
// CUSTOMERS (25)
// ═══════════════════════════════════════════════════════════════════
const CUSTOMER_DEFS = [
  { name: 'Midwest Auto Parts Inc', vertical: 'manufacturing', city: 'Detroit', state: 'MI' },
  { name: 'Pacific Fresh Foods LLC', vertical: 'food_bev', city: 'Los Angeles', state: 'CA' },
  { name: 'MedSupply Direct', vertical: 'pharma', city: 'Memphis', state: 'TN' },
  { name: 'TechVault Electronics', vertical: 'ecommerce', city: 'Austin', state: 'TX' },
  { name: 'Great Lakes Steel Corp', vertical: 'manufacturing', city: 'Cleveland', state: 'OH' },
  { name: 'SunCoast Beverages', vertical: 'food_bev', city: 'Tampa', state: 'FL' },
  { name: 'Alpine Pharma Group', vertical: 'pharma', city: 'Denver', state: 'CO' },
  { name: 'QuickShip E-Commerce', vertical: 'ecommerce', city: 'Newark', state: 'NJ' },
  { name: 'Southern Lumber Supply', vertical: 'manufacturing', city: 'Atlanta', state: 'GA' },
  { name: 'FreshHarvest Organics', vertical: 'food_bev', city: 'Sacramento', state: 'CA' },
  { name: 'BioGen Therapeutics', vertical: 'pharma', city: 'Boston', state: 'MA' },
  { name: 'Cascade Paper Mills', vertical: 'manufacturing', city: 'Portland', state: 'OR' },
  { name: 'Metro Fashion Group', vertical: 'retail', city: 'New York', state: 'NY' },
  { name: 'Heartland Grain Co', vertical: 'food_bev', city: 'Kansas City', state: 'MO' },
  { name: 'PrimeCare Medical', vertical: 'pharma', city: 'Philadelphia', state: 'PA' },
  { name: 'Gulf Petrochemicals', vertical: 'manufacturing', city: 'Houston', state: 'TX' },
  { name: 'NovaTech Components', vertical: 'manufacturing', city: 'Phoenix', state: 'AZ' },
  { name: 'Coastal Wine & Spirits', vertical: 'food_bev', city: 'San Francisco', state: 'CA' },
  { name: 'DataStream Servers', vertical: 'ecommerce', city: 'Dallas', state: 'TX' },
  { name: 'Pinnacle Construction', vertical: 'manufacturing', city: 'Charlotte', state: 'NC' },
  { name: 'Garden State Produce', vertical: 'food_bev', city: 'Newark', state: 'NJ' },
  { name: 'Redwood Chemical Corp', vertical: 'manufacturing', city: 'Oakland', state: 'CA' },
  { name: 'Summit Sports Equipment', vertical: 'retail', city: 'Denver', state: 'CO' },
  { name: 'Heritage Furniture Co', vertical: 'retail', city: 'High Point', state: 'NC' },
  { name: 'Precision Aerospace LLC', vertical: 'manufacturing', city: 'Wichita', state: 'KS' },
];

export const EXPANDED_CUSTOMERS = CUSTOMER_DEFS.map((c, i) => ({
  id: uid('cust', i + 1), name: c.name, vertical: c.vertical,
  address: { city: c.city, state: c.state, country: 'US' },
  contactName: `${['James', 'Sarah', 'Mike', 'Lisa', 'David', 'Amy', 'Robert', 'Karen'][i % 8]} ${['Wilson', 'Chen', 'Rodriguez', 'Kim', 'Patel', 'Johnson', 'Lee', 'Brown'][i % 8]}`,
  contactEmail: `${c.name.split(' ')[0].toLowerCase()}@${c.name.split(' ').slice(0, 2).join('').toLowerCase().replace(/[^a-z]/g, '')}.com`,
  contactPhone: `(${randBetween(200, 999)}) ${randBetween(100, 999)}-${pad(randBetween(1000, 9999), 4)}`,
  creditLimit: randBetween(5, 50) * 10000, paymentTerms: pick(['Net 30', 'Net 45', 'Net 60', 'Due on Receipt']),
  status: i < 22 ? 'active' : 'inactive',
  ...uf({ customerId: uid('cust', i + 1) }, randBetween(30, 90)),
}));

// ═══════════════════════════════════════════════════════════════════
// CARRIERS (40)
// ═══════════════════════════════════════════════════════════════════
const CARRIER_DEFS = [
  { name: 'Swift Transportation', scac: 'SWFT', mode: 'OTR', mc: 'MC-116325', dot: 'DOT-468390' },
  { name: 'Werner Enterprises', scac: 'WERN', mode: 'OTR', mc: 'MC-133034', dot: 'DOT-505409' },
  { name: 'J.B. Hunt Transport', scac: 'JBHT', mode: 'OTR', mc: 'MC-67341', dot: 'DOT-510508' },
  { name: 'Schneider National', scac: 'SNDR', mode: 'OTR', mc: 'MC-133655', dot: 'DOT-431041' },
  { name: 'Heartland Express', scac: 'HTLD', mode: 'OTR', mc: 'MC-215917', dot: 'DOT-587345' },
  { name: 'USA Truck', scac: 'USAT', mode: 'OTR', mc: 'MC-150900', dot: 'DOT-518529' },
  { name: 'PAM Transport', scac: 'PAMT', mode: 'OTR', mc: 'MC-219382', dot: 'DOT-596139' },
  { name: 'Covenant Transport', scac: 'CVNT', mode: 'OTR', mc: 'MC-210974', dot: 'DOT-587956' },
  { name: 'Marten Transport', scac: 'MRTN', mode: 'OTR', mc: 'MC-142650', dot: 'DOT-457844' },
  { name: 'Knight-Swift', scac: 'KNIS', mode: 'OTR', mc: 'MC-138810', dot: 'DOT-456923' },
  { name: 'Old Dominion Freight', scac: 'ODFL', mode: 'LTL', mc: 'MC-64722', dot: 'DOT-110775' },
  { name: 'XPO Logistics', scac: 'CNWY', mode: 'LTL', mc: 'MC-79853', dot: 'DOT-193045' },
  { name: 'Estes Express Lines', scac: 'EXLA', mode: 'LTL', mc: 'MC-69047', dot: 'DOT-111741' },
  { name: 'Saia Inc', scac: 'SAIA', mode: 'LTL', mc: 'MC-145413', dot: 'DOT-471044' },
  { name: 'ABF Freight', scac: 'ABFS', mode: 'LTL', mc: 'MC-29891', dot: 'DOT-36024' },
  { name: 'FedEx Freight', scac: 'FXFE', mode: 'LTL', mc: 'MC-345930', dot: 'DOT-1037780' },
  { name: 'TForce Freight', scac: 'TFRC', mode: 'LTL', mc: 'MC-208266', dot: 'DOT-572860' },
  { name: 'R+L Carriers', scac: 'RLCA', mode: 'LTL', mc: 'MC-97234', dot: 'DOT-287079' },
  { name: 'Hub Group Inc', scac: 'HUBG', mode: 'Intermodal', mc: 'MC-242897', dot: 'DOT-641506' },
  { name: 'J.B. Hunt Intermodal', scac: 'JBIM', mode: 'Intermodal', mc: 'MC-67341', dot: 'DOT-510508' },
  { name: 'Schneider Intermodal', scac: 'SNIM', mode: 'Intermodal', mc: 'MC-133655', dot: 'DOT-431041' },
  { name: 'Maersk Line', scac: 'MAEU', mode: 'Ocean', mc: null, dot: null },
  { name: 'COSCO Shipping', scac: 'COSU', mode: 'Ocean', mc: null, dot: null },
  { name: 'MSC Mediterranean', scac: 'MSCU', mode: 'Ocean', mc: null, dot: null },
  { name: 'CMA CGM Group', scac: 'CMDU', mode: 'Ocean', mc: null, dot: null },
  { name: 'Hapag-Lloyd AG', scac: 'HLCU', mode: 'Ocean', mc: null, dot: null },
  { name: 'ONE (Ocean Network)', scac: 'ONEY', mode: 'Ocean', mc: null, dot: null },
  { name: 'Evergreen Marine', scac: 'EGLV', mode: 'Ocean', mc: null, dot: null },
  { name: 'FedEx Express', scac: 'FDEG', mode: 'Air', mc: null, dot: null },
  { name: 'UPS Airlines', scac: 'UPSN', mode: 'Air', mc: null, dot: null },
  { name: 'DHL Express', scac: 'DHLX', mode: 'Air', mc: null, dot: null },
  { name: 'Atlas Air', scac: 'ATLA', mode: 'Air', mc: null, dot: null },
  { name: 'Total Quality Logistics', scac: 'TQLA', mode: 'OTR', mc: 'MC-381570', dot: 'DOT-1065027' },
  { name: 'Echo Global Logistics', scac: 'ECHO', mode: 'OTR', mc: 'MC-354781', dot: 'DOT-972816' },
  { name: 'NFI Industries', scac: 'NFII', mode: 'Drayage', mc: 'MC-171287', dot: 'DOT-528923' },
  { name: 'Dray Alliance', scac: 'DRYA', mode: 'Drayage', mc: 'MC-587234', dot: 'DOT-1432876' },
  { name: 'IMC Companies', scac: 'IMCC', mode: 'Drayage', mc: 'MC-175489', dot: 'DOT-542187' },
  { name: 'ContainerPort Group', scac: 'CPGP', mode: 'Drayage', mc: 'MC-298765', dot: 'DOT-814532' },
  { name: 'UPS Ground', scac: 'UPSG', mode: 'Parcel', mc: null, dot: null },
  { name: 'FedEx Ground', scac: 'FXGR', mode: 'Parcel', mc: null, dot: null },
];

export const EXPANDED_CARRIERS = CARRIER_DEFS.map((c, i) => ({
  id: uid('carr', i + 1), name: c.name, scac: c.scac, primaryMode: c.mode,
  mcNumber: c.mc, dotNumber: c.dot,
  contactName: `${pick(['John', 'Maria', 'Tom', 'Linda', 'Kevin', 'Jessica'])} ${pick(['Smith', 'Jones', 'Davis', 'Garcia', 'Martinez'])}`,
  contactPhone: `(${randBetween(200, 999)}) ${randBetween(100, 999)}-${pad(randBetween(1000, 9999), 4)}`,
  contactEmail: `dispatch@${c.name.split(' ')[0].toLowerCase()}.com`,
  equipmentTypes: c.mode === 'OTR' ? ['Dry Van 53ft', 'Reefer 53ft'] : c.mode === 'LTL' ? ['LTL Consolidated'] : [c.mode],
  rating: randFloat(3.5, 5.0), onTimePercent: randFloat(85, 99),
  insuranceExpiry: daysAgo(-randBetween(30, 365)),
  status: i < 37 ? 'active' : 'inactive',
  ...uf({}, randBetween(30, 180)),
}));

// ═══════════════════════════════════════════════════════════════════
// LANES (for load generation)
// ═══════════════════════════════════════════════════════════════════
const LANES = [
  { o: 'Chicago, IL', d: 'Dallas, TX', miles: 920 }, { o: 'Los Angeles, CA', d: 'New York, NY', miles: 2790 },
  { o: 'Miami, FL', d: 'Atlanta, GA', miles: 662 }, { o: 'Houston, TX', d: 'Memphis, TN', miles: 586 },
  { o: 'Detroit, MI', d: 'Charlotte, NC', miles: 630 }, { o: 'Seattle, WA', d: 'Denver, CO', miles: 1321 },
  { o: 'Phoenix, AZ', d: 'Kansas City, MO', miles: 1175 }, { o: 'Newark, NJ', d: 'Boston, MA', miles: 215 },
  { o: 'Sacramento, CA', d: 'Portland, OR', miles: 581 }, { o: 'Tampa, FL', d: 'Nashville, TN', miles: 672 },
  { o: 'Philadelphia, PA', d: 'Cleveland, OH', miles: 431 }, { o: 'San Francisco, CA', d: 'Las Vegas, NV', miles: 569 },
  { o: 'Atlanta, GA', d: 'Jacksonville, FL', miles: 346 }, { o: 'Dallas, TX', d: 'Oklahoma City, OK', miles: 207 },
  { o: 'Minneapolis, MN', d: 'Chicago, IL', miles: 410 },
];

const COMMODITIES_BY_MODE = {
  OTR: ['Auto Parts', 'Electronics', 'Furniture', 'Machinery', 'Paper Products', 'Building Materials', 'Textiles', 'Plastics'],
  LTL: ['Mixed Freight', 'Small Machinery', 'Office Supplies', 'Retail Goods', 'Industrial Parts', 'Medical Devices'],
  Intermodal: ['Consumer Goods', 'Retail Import', 'Auto Components', 'Bulk Commodities', 'Grain Products'],
  Drayage: ['Import Containers', 'Export Containers', 'Reefer Cargo', 'Overweight Container'],
  Ocean: ['Footwear', 'Electronics', 'Furniture', 'Textiles', 'Raw Materials', 'Frozen Seafood', 'Pharmaceuticals'],
  Air: ['High-Value Electronics', 'Pharmaceuticals', 'Perishables', 'Emergency Parts', 'Fashion Samples'],
  Parcel: ['E-Commerce Orders', 'Documents', 'Small Parts', 'Samples', 'Returns'],
};

const STATUSES_BY_DIST = [
  ...Array(60).fill('delivered'), ...Array(25).fill('in_transit'), ...Array(8).fill('confirmed'),
  ...Array(4).fill('tendered'), ...Array(3).fill('created'),
];
// Override for more variety
const ALL_LOAD_STATUSES = ['created', 'tendered', 'confirmed', 'picking_up', 'in_transit', 'delivered', 'closed', 'cancelled'];

// ═══════════════════════════════════════════════════════════════════
// LOADS (150)
// ═══════════════════════════════════════════════════════════════════
const MODES_DIST = [
  ...Array(45).fill('OTR'), ...Array(25).fill('LTL'), ...Array(20).fill('Intermodal'),
  ...Array(15).fill('Drayage'), ...Array(20).fill('Ocean'), ...Array(15).fill('Air'), ...Array(10).fill('Parcel'),
];

function generateLoad(i) {
  const mode = MODES_DIST[i % MODES_DIST.length];
  const lane = LANES[i % LANES.length];
  const cust = EXPANDED_CUSTOMERS[i % EXPANDED_CUSTOMERS.length];
  const modeCarriers = EXPANDED_CARRIERS.filter(c => c.primaryMode === mode);
  const carrier = modeCarriers.length > 0 ? modeCarriers[i % modeCarriers.length] : EXPANDED_CARRIERS[i % EXPANDED_CARRIERS.length];
  const dBack = randBetween(1, 88);
  const status = pick(STATUSES_BY_DIST);

  const rateRanges = { OTR: [1200, 4500], LTL: [350, 1800], Intermodal: [1800, 3500], Drayage: [500, 1200], Ocean: [2500, 8000], Air: [4000, 15000], Parcel: [15, 85] };
  const weightRanges = { OTR: [10000, 44000], LTL: [500, 9999], Intermodal: [20000, 44000], Drayage: [15000, 44000], Ocean: [5000, 45000], Air: [100, 5000], Parcel: [1, 150] };

  const [rLo, rHi] = rateRanges[mode];
  const [wLo, wHi] = weightRanges[mode];
  const custRate = randBetween(rLo, rHi);
  const carrRate = Math.round(custRate * randFloat(0.65, 0.85));
  const margin = custRate - carrRate;

  return {
    id: uid('load', i + 1),
    loadNumber: `WC-${pad(10700 + i)}`,
    mode,
    status,
    customerName: cust.name,
    customerId: cust.id,
    carrierName: carrier.name,
    carrierId: carrier.id,
    carrierScac: carrier.scac,
    equipmentType: mode === 'OTR' ? pick(['Dry Van 53ft', 'Reefer 53ft', 'Flatbed 48ft']) : mode === 'LTL' ? 'LTL Consolidated' : mode === 'Parcel' ? 'Parcel' : '40ft Container',
    commodity: pick(COMMODITIES_BY_MODE[mode]),
    weight: randBetween(wLo, wHi),
    origin: { city: lane.o.split(', ')[0], state: lane.o.split(', ')[1] },
    destination: { city: lane.d.split(', ')[0], state: lane.d.split(', ')[1] },
    miles: lane.miles + randBetween(-50, 50),
    pickupDate: daysAgo(dBack),
    deliveryDate: status === 'delivered' ? daysAgo(dBack - randBetween(1, 5)) : null,
    estimatedDelivery: daysAgo(dBack - randBetween(1, 7)),
    financials: { customerRate: custRate, carrierRate: carrRate, margin, marginPercent: parseFloat(((margin / custRate) * 100).toFixed(1)), totalRevenue: custRate, totalCost: carrRate },
    driver: mode === 'OTR' || mode === 'LTL' || mode === 'Drayage' ? { driverName: `${pick(['Tony', 'Carlos', 'Pete', 'Miguel', 'Jake', 'Robert', 'Maria', 'Kevin'])} ${pick(['Reyes', 'Mendez', 'Walsh', 'Santos', 'Williams', 'Chen', 'Rivera', 'Pham'])}`, driverPhone: `(${randBetween(200, 999)}) 555-${pad(randBetween(100, 999), 4)}` } : null,
    // TMS-TRACK / CX-3: canonical visibility fields derived from current status.
    milestones:   buildMilestones(status, dBack),
    aegisEta:     buildAegisEta(status, dBack, custRate, margin),
    slaDueAt:     new Date(Date.now() - (dBack - 6) * 86400_000).toISOString(),
    exceptions:   buildExceptions(status, i),
    documents:    buildDocuments(status),
    ...uf({ customerId: cust.id }, dBack),
  };
}

// ── CX-3 / TMS-TRACK helpers ─────────────────────────────────────────────
const _MILESTONE_LADDER = ['booked', 'picked_up', 'in_transit', 'checkpoint', 'out_for_delivery', 'delivered', 'pod_uploaded', 'invoiced'];
function _milestonesForStatus(status) {
  switch (status) {
    case 'created':    return [];
    case 'tendered':   return ['booked'];
    case 'confirmed':  return ['booked'];
    case 'picking_up': return ['booked', 'picked_up'];
    case 'in_transit': return ['booked', 'picked_up', 'in_transit'];
    case 'delivered':  return ['booked', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered', 'pod_uploaded'];
    case 'closed':     return _MILESTONE_LADDER;
    case 'cancelled':  return ['booked'];
    default:           return [];
  }
}
function buildMilestones(status, daysBack) {
  const codes = _milestonesForStatus(status);
  const base = Date.now() - daysBack * 86400_000;
  return codes.map((code, i) => ({
    code,
    at: new Date(base + i * 6 * 3600_000).toISOString(),
    source: i === 0 ? 'system' : i < 3 ? 'driver' : 'gps',
  }));
}
function buildAegisEta(status, daysBack, custRate, margin) {
  if (status === 'delivered' || status === 'closed' || status === 'cancelled') return null;
  const baseDelivery = Date.now() + Math.max(0, 5 - daysBack) * 86400_000;
  const confidence = Math.min(0.98, Math.max(0.55, 0.70 + (margin / (custRate || 1))));
  return {
    etaAt: new Date(baseDelivery + randBetween(-4, 8) * 3600_000).toISOString(),
    confidence: parseFloat(confidence.toFixed(2)),
    rationale: 'AEGIS modeled ETA from GPS + route + carrier on-time performance.',
    source: 'aegis',
  };
}
function buildExceptions(status, seed) {
  if (status !== 'in_transit') return [];
  // ~15% of in-transit loads have an open exception
  if ((seed % 7) !== 0) return [];
  return [{
    code: 'eta_delay',
    label: 'Weather delay — I-40 corridor',
    severity: (seed % 14 === 0) ? 'high' : 'medium',
    openedAt: new Date(Date.now() - randBetween(1, 12) * 3600_000).toISOString(),
  }];
}
function buildDocuments(status) {
  const docs = [];
  if (['picking_up','in_transit','out_for_delivery','delivered','closed'].includes(status)) {
    docs.push({ code: 'BOL', label: 'Bill of Lading', url: '#' });
  }
  if (['delivered','closed'].includes(status)) {
    docs.push({ code: 'POD', label: 'Proof of Delivery', url: '#' });
  }
  if (status === 'closed') {
    docs.push({ code: 'INV', label: 'Invoice', url: '#' });
  }
  return docs;
}

export const EXPANDED_LOADS = Array.from({ length: 150 }, (_, i) => generateLoad(i));

// ═══════════════════════════════════════════════════════════════════
// QUOTES (60)
// ═══════════════════════════════════════════════════════════════════
// TMS-AUTOSEED-SEAT-F-PARITY-1 (2026-05-28): added 'accepted' segment so the New Load
// wizard's "From Accepted Quote" path has data to exercise on a fresh dev seed.
// Distribution still sums to 60 (10 + 15 + 10 + 5 + 10 + 10).
const QUOTE_STATUS_DIST = [
  ...Array(10).fill('draft'),
  ...Array(15).fill('pending_approval'),
  ...Array(10).fill('approved'),
  ...Array(5).fill('accepted'),
  ...Array(10).fill('rejected'),
  ...Array(10).fill('converted'),
];

export const EXPANDED_QUOTES = Array.from({ length: 60 }, (_, i) => {
  const cust = EXPANDED_CUSTOMERS[i % EXPANDED_CUSTOMERS.length];
  const lane = LANES[i % LANES.length];
  const mode = pick(['OTR', 'LTL', 'Intermodal', 'Ocean', 'Air']);
  const rate = randBetween(800, 12000);
  const status = QUOTE_STATUS_DIST[i % QUOTE_STATUS_DIST.length];
  return {
    id: uid('qt', i + 1), quoteNumber: `QT-${pad(10020 + i)}`, status, mode,
    customerName: cust.name, customerId: cust.id,
    origin: lane.o, destination: lane.d,
    equipment: pick(['Dry Van 53ft', 'Reefer 53ft', 'Flatbed 48ft', '40ft Container']),
    commodity: pick(COMMODITIES_BY_MODE[mode] || ['General Freight']),
    weight: randBetween(1000, 44000),
    rate, margin: Math.round(rate * 0.25), marginPercent: 25,
    validUntil: daysAgo(-randBetween(3, 30)),
    notes: '',
    ...uf({ customerId: cust.id, quoteId: uid('qt', i + 1) }, randBetween(1, 60)),
  };
});

// ═══════════════════════════════════════════════════════════════════
// PURCHASE ORDERS (40)
// ═══════════════════════════════════════════════════════════════════
const PO_STATUS_DIST = ['draft', 'submitted', 'acknowledged', 'in_production', 'ready_to_ship', 'shipped', 'received', 'closed'];
const PRODUCTS = [
  { sku: 'AP-4401', desc: 'Brake Rotors - Front', uom: 'EA', price: 45 },
  { sku: 'AP-4402', desc: 'Oil Filter Assembly', uom: 'EA', price: 12 },
  { sku: 'EL-2201', desc: 'Circuit Board Module', uom: 'EA', price: 89 },
  { sku: 'FD-1100', desc: 'Organic Quinoa 25lb', uom: 'CS', price: 38 },
  { sku: 'FD-1101', desc: 'Almond Butter 16oz', uom: 'CS', price: 72 },
  { sku: 'PH-3301', desc: 'Surgical Gloves Box', uom: 'CS', price: 28 },
  { sku: 'PH-3302', desc: 'IV Saline Bags 1L', uom: 'CS', price: 95 },
  { sku: 'TX-5501', desc: 'Cotton T-Shirts Assorted', uom: 'CS', price: 120 },
  { sku: 'BM-6601', desc: 'Lumber 2x4x8 Bundle', uom: 'BDL', price: 340 },
  { sku: 'CH-7701', desc: 'Industrial Solvent 5gal', uom: 'DR', price: 165 },
];

export const EXPANDED_PURCHASE_ORDERS = Array.from({ length: 40 }, (_, i) => {
  const cust = EXPANDED_CUSTOMERS[i % EXPANDED_CUSTOMERS.length];
  const status = PO_STATUS_DIST[i % PO_STATUS_DIST.length];
  const numLines = randBetween(1, 4);
  const lineItems = Array.from({ length: numLines }, (_, j) => {
    const prod = PRODUCTS[(i * 3 + j) % PRODUCTS.length];
    const qty = randBetween(10, 500);
    return { lineNumber: j + 1, description: prod.desc, sku: prod.sku, quantity: qty, unitOfMeasure: prod.uom, unitPrice: prod.price, totalPrice: qty * prod.price };
  });
  const totalValue = lineItems.reduce((s, li) => s + li.totalPrice, 0);
  const dBack = randBetween(5, 70);
  const linkedLoad = status === 'shipped' || status === 'received' ? EXPANDED_LOADS[i % EXPANDED_LOADS.length]?.id : null;

  return {
    id: uid('po', i + 1), poNumber: `PO-${pad(20001 + i)}`, customerId: cust.id,
    vendorName: pick(['Zhejiang Manufacturing', 'Houston Industrial Supply', 'Great Plains Ag Products', 'Medline Industries', 'Uline Packaging']),
    status, lineItems, totalValue,
    requestedShipDate: daysAgo(dBack - 10), requiredDeliveryDate: daysAgo(dBack - 20),
    shipToAddress: { street: `${randBetween(100, 9999)} ${pick(['Industrial', 'Commerce', 'Distribution', 'Warehouse'])} Dr`, city: cust.address.city, state: cust.address.state, zip: `${randBetween(10000, 99999)}`, country: 'US' },
    linkedLoadId: linkedLoad, notes: '',
    ...uf({ customerId: cust.id }, dBack),
  };
});

// ═══════════════════════════════════════════════════════════════════
// APPOINTMENTS (50)
// ═══════════════════════════════════════════════════════════════════
const APT_STATUS_DIST = [...Array(8).fill('scheduled'), ...Array(8).fill('confirmed'), ...Array(5).fill('in_progress'), ...Array(20).fill('completed'), ...Array(5).fill('missed'), ...Array(4).fill('rescheduled')];
const FACILITIES = [
  'Midwest Distribution Center', 'Pacific Cold Storage', 'Gulf Coast Warehouse', 'Northeast Fulfillment Hub',
  'Southeast Cross-Dock', 'Mountain Region DC', 'Great Lakes Terminal', 'Desert Sun Logistics Park',
];
const TIME_WINDOWS = [{ start: '06:00', end: '10:00' }, { start: '08:00', end: '12:00' }, { start: '10:00', end: '14:00' }, { start: '12:00', end: '16:00' }, { start: '14:00', end: '18:00' }];

export const EXPANDED_APPOINTMENTS = Array.from({ length: 50 }, (_, i) => {
  const load = EXPANDED_LOADS[i % EXPANDED_LOADS.length];
  const status = APT_STATUS_DIST[i % APT_STATUS_DIST.length];
  const dBack = randBetween(0, 60);
  const tw = TIME_WINDOWS[i % TIME_WINDOWS.length];
  const isPickup = i % 2 === 0;
  const loc = isPickup ? load.origin : load.destination;

  return {
    id: uid('apt', i + 1), appointmentNumber: `APT-${pad(30001 + i)}`,
    loadId: load.id, type: isPickup ? 'pickup' : 'delivery',
    facilityName: FACILITIES[i % FACILITIES.length],
    facilityAddress: { street: `${randBetween(100, 9999)} Logistics Blvd`, city: loc.city, state: loc.state, zip: `${randBetween(10000, 99999)}`, country: 'US' },
    scheduledDate: daysAgo(dBack), scheduledTimeWindow: tw,
    actualArrival: status === 'completed' || status === 'in_progress' ? daysAgo(dBack) : null,
    actualDeparture: status === 'completed' ? daysAgo(dBack) : null,
    status, dockNumber: status !== 'scheduled' ? `Dock ${randBetween(1, 12)}` : null,
    confirmationNumber: status !== 'scheduled' ? `CNF-${randBetween(100000, 999999)}` : null,
    notes: status === 'missed' ? 'Driver no-show — rescheduling required' : '',
    ...uf({ loadId: load.id }, dBack),
  };
});

// ═══════════════════════════════════════════════════════════════════
// CARRIER VETTING (20)
// ═══════════════════════════════════════════════════════════════════
export const EXPANDED_CARRIER_VETTING = Array.from({ length: 20 }, (_, i) => {
  const carrier = EXPANDED_CARRIERS[i % EXPANDED_CARRIERS.length];
  const score = randBetween(60, 100);
  return {
    id: uid('vet', i + 1), carrierId: carrier.id, carrierName: carrier.name, scac: carrier.scac,
    safetyScore: score, insuranceStatus: score > 75 ? 'valid' : 'expiring_soon',
    insuranceExpiry: daysAgo(-randBetween(10, 365)),
    safetyRating: score >= 90 ? 'satisfactory' : score >= 75 ? 'conditional' : 'unsatisfactory',
    csa2010Score: randBetween(0, 100), outOfServiceRate: randFloat(0, 15),
    lastAuditDate: daysAgo(randBetween(10, 180)),
    authorityStatus: 'active', dotInspections: randBetween(5, 50),
    crashRate: randFloat(0, 3), driverFitness: randBetween(70, 100),
    ...uf({}, randBetween(5, 90)),
  };
});

// ═══════════════════════════════════════════════════════════════════
// IOT TRACKERS (15)
// ═══════════════════════════════════════════════════════════════════
// IOT-DEMO-SEED-1: kairos-demo trackers prepended for DeviceManagement fallback.
const _baseExpandedTrackers = Array.from({ length: 15 }, (_, i) => {
  const load = EXPANDED_LOADS[i % EXPANDED_LOADS.length];
  return {
    id: uid('trk', i + 1), trackerSerial: `WM-TRK-${pad(1001 + i)}`,
    type: pick(['temperature', 'gps', 'multi_sensor', 'shock']),
    status: i < 12 ? 'active' : 'inactive',
    loadId: load.id, loadNumber: load.loadNumber,
    lastReading: { temperature: randFloat(32, 78), humidity: randFloat(20, 80), lat: randFloat(25, 48), lng: randFloat(-122, -71), battery: randBetween(15, 100) },
    lastEventTime: hoursAgo(randBetween(1, 48)),
    config: { tempMin: 33, tempMax: 72, humidityMax: 75, shockThreshold: 3.5 },
    ...uf({ loadId: load.id }, randBetween(1, 60)),
  };
});
export const EXPANDED_IOT_TRACKERS = [...KAIROS_DEMO_DM_TRACKERS, ..._baseExpandedTrackers];

// ═══════════════════════════════════════════════════════════════════
// IOT EVENTS (200)
// ═══════════════════════════════════════════════════════════════════
const IOT_EVENT_TYPES = ['temperature', 'gps', 'humidity', 'shock', 'light', 'battery'];
export const EXPANDED_IOT_EVENTS = Array.from({ length: 200 }, (_, i) => {
  const tracker = EXPANDED_IOT_TRACKERS[i % EXPANDED_IOT_TRACKERS.length];
  const type = IOT_EVENT_TYPES[i % IOT_EVENT_TYPES.length];
  const hBack = randBetween(1, 720); // up to 30 days
  return {
    id: uid('iot-evt', i + 1), trackerId: tracker.id, trackerSerial: tracker.trackerSerial,
    loadId: tracker.loadId, eventType: type,
    value: type === 'temperature' ? randFloat(28, 82) : type === 'humidity' ? randFloat(15, 90) : type === 'shock' ? randFloat(0.5, 8.0) : type === 'light' ? randBetween(0, 1000) : type === 'battery' ? randBetween(5, 100) : randFloat(25, 48),
    unit: type === 'temperature' ? '°F' : type === 'humidity' ? '%' : type === 'shock' ? 'g' : type === 'light' ? 'lux' : type === 'battery' ? '%' : '',
    lat: randFloat(25, 48), lng: randFloat(-122, -71),
    isAlert: type === 'temperature' ? (Math.random() < 0.15) : type === 'shock' ? (Math.random() < 0.1) : false,
    timestamp: hoursAgo(hBack),
    ...uf({ loadId: tracker.loadId }, Math.ceil(hBack / 24)),
  };
});

// ═══════════════════════════════════════════════════════════════════
// ASSETS (20)
// ═══════════════════════════════════════════════════════════════════
// IOT-DEMO-SEED-1: kairos-demo trailers prepended for DigitalTwin / MultiSensorView / ThresholdConfig fallback.
const _baseExpandedAssets = Array.from({ length: 20 }, (_, i) => {
  const type = i < 10 ? 'trailer' : i < 16 ? 'container' : 'chassis';
  return {
    id: uid('ast', i + 1), assetNumber: `${type === 'trailer' ? 'TRL' : type === 'container' ? 'CTR' : 'CHS'}-${pad(5001 + i)}`,
    type, status: i < 17 ? 'available' : 'in_use',
    make: type === 'trailer' ? pick(['Wabash', 'Great Dane', 'Utility', 'Hyundai']) : type === 'container' ? pick(['CIMC', 'Maersk', 'Triton']) : pick(['DCLI', 'Flexi-Van', 'TPNZ']),
    year: randBetween(2018, 2025), length: type === 'trailer' ? '53ft' : type === 'container' ? pick(['20ft', '40ft', '40ft HC']) : '40ft',
    condition: pick(['excellent', 'good', 'fair']),
    lastInspection: daysAgo(randBetween(5, 90)),
    currentLocation: pick(['Chicago Yard', 'LA Terminal', 'Newark Hub', 'Houston Depot', 'Atlanta DC']),
    assignedLoadId: i < 8 ? EXPANDED_LOADS[i]?.id : null,
    ...uf({}, randBetween(30, 365)),
  };
});
export const EXPANDED_ASSETS = [...KAIROS_DEMO_TRAILERS, ..._baseExpandedAssets];

// ═══════════════════════════════════════════════════════════════════
// TRUCKS (12)
// ═══════════════════════════════════════════════════════════════════
export const EXPANDED_TRUCKS = Array.from({ length: 12 }, (_, i) => ({
  id: uid('truck', i + 1), truckNumber: `WM-${pad(7001 + i)}`,
  make: pick(['Freightliner', 'Peterbilt', 'Kenworth', 'Volvo', 'International']),
  model: pick(['Cascadia', '389', 'T680', 'VNL', 'LT']),
  year: randBetween(2019, 2025), vin: `1FU${String.fromCharCode(65 + i)}${randBetween(10000, 99999)}GH${randBetween(100000, 999999)}`,
  status: i < 10 ? 'active' : 'maintenance',
  currentDriver: `${pick(['Tony', 'Carlos', 'Pete', 'Miguel', 'Jake', 'Robert', 'Maria', 'Kevin', 'Sam', 'Lisa', 'Tom', 'Anna'])} ${pick(['Reyes', 'Mendez', 'Walsh', 'Santos', 'Williams', 'Chen'])}`,
  lastLocation: { city: pick(['Chicago', 'Dallas', 'LA', 'Newark', 'Atlanta', 'Houston']), state: pick(['IL', 'TX', 'CA', 'NJ', 'GA', 'TX']), lat: randFloat(29, 42), lng: randFloat(-118, -74) },
  mileage: randBetween(50000, 450000), fuelLevel: randBetween(20, 100),
  nextService: daysAgo(-randBetween(5, 60)),
  ...uf({}, randBetween(30, 365)),
}));

// ═══════════════════════════════════════════════════════════════════
// FINANCIALS (80)
// ═══════════════════════════════════════════════════════════════════
const FIN_TYPES = ['invoice', 'payment', 'credit_memo', 'debit_memo'];
const FIN_STATUSES = ['draft', 'issued', 'sent', 'partially_paid', 'paid', 'overdue', 'void'];

export const EXPANDED_FINANCIALS = Array.from({ length: 80 }, (_, i) => {
  const load = EXPANDED_LOADS[i % EXPANDED_LOADS.length];
  const type = i < 50 ? 'invoice' : i < 65 ? 'payment' : pick(['credit_memo', 'debit_memo']);
  const amount = type === 'invoice' ? load.financials.customerRate : type === 'payment' ? load.financials.customerRate : randBetween(50, 500);
  const dBack = randBetween(1, 75);
  return {
    id: uid('fin', i + 1), documentNumber: `${type === 'invoice' ? 'INV' : type === 'payment' ? 'PMT' : 'CM'}-${pad(30001 + i)}`,
    type, loadId: load.id, loadNumber: load.loadNumber, customerId: load.customerId, customerName: load.customerName,
    amount, currency: 'USD',
    status: type === 'invoice' ? pick(['issued', 'sent', 'partially_paid', 'paid', 'overdue']) : type === 'payment' ? 'paid' : 'issued',
    dueDate: type === 'invoice' ? daysAgo(dBack - 30) : null,
    paidDate: type === 'payment' || (type === 'invoice' && Math.random() < 0.5) ? daysAgo(dBack - randBetween(1, 15)) : null,
    paymentMethod: type === 'payment' ? pick(['ACH', 'Wire', 'Check', 'Credit Card']) : null,
    notes: '',
    ...uf({ loadId: load.id, customerId: load.customerId }, dBack),
  };
});

// ═══════════════════════════════════════════════════════════════════
// EVENTS (100) — system/user/AEGIS actions
// ═══════════════════════════════════════════════════════════════════
const EVENT_ACTIONS = [
  'load.created', 'load.status_changed', 'load.assigned_carrier', 'quote.created', 'quote.approved',
  'quote.rejected', 'appointment.scheduled', 'appointment.completed', 'appointment.missed',
  'iot.alert_triggered', 'iot.breach_detected', 'aegis.recommendation', 'aegis.auto_match',
  'user.login', 'document.uploaded', 'invoice.generated', 'payment.received', 'carrier.vetted',
  'exception.created', 'exception.resolved',
];

export const EXPANDED_EVENTS = Array.from({ length: 100 }, (_, i) => {
  const action = EVENT_ACTIONS[i % EVENT_ACTIONS.length];
  const load = EXPANDED_LOADS[i % EXPANDED_LOADS.length];
  return {
    id: uid('evt', i + 1), action, resourceType: action.split('.')[0], resourceId: load.id,
    actor: action.startsWith('aegis') ? 'aegis' : action.startsWith('user') ? 'user-001' : 'system',
    description: `${action.replace('.', ': ').replace('_', ' ')} — ${load.loadNumber}`,
    metadata: { loadNumber: load.loadNumber, mode: load.mode },
    timestamp: hoursAgo(randBetween(1, 2000)),
    ...uf({ loadId: load.id }, Math.ceil(randBetween(1, 2000) / 24)),
  };
});

// ═══════════════════════════════════════════════════════════════════
// EXCEPTIONS (30)
// ═══════════════════════════════════════════════════════════════════
const EXCEPTION_TYPES = ['delay', 'damage', 'temperature_breach', 'customs_hold', 'missing_docs', 'overweight', 'detention', 'wrong_delivery', 'refused_shipment', 'mechanical_breakdown'];

export const EXPANDED_EXCEPTIONS = Array.from({ length: 30 }, (_, i) => {
  const load = EXPANDED_LOADS[i % EXPANDED_LOADS.length];
  const type = EXCEPTION_TYPES[i % EXCEPTION_TYPES.length];
  const severity = type === 'temperature_breach' || type === 'damage' ? 'critical' : type === 'delay' || type === 'detention' ? 'warning' : 'high';
  return {
    id: uid('exc', i + 1), exceptionNumber: `EXC-${pad(40001 + i)}`,
    loadId: load.id, loadNumber: load.loadNumber, customerId: load.customerId, customerName: load.customerName,
    type, severity, status: i < 20 ? 'resolved' : i < 25 ? 'in_progress' : 'open',
    description: `${type.replace(/_/g, ' ')} on ${load.loadNumber} — ${load.origin.city} to ${load.destination.city}`,
    reportedAt: daysAgo(randBetween(1, 60)),
    resolvedAt: i < 20 ? daysAgo(randBetween(0, 5)) : null,
    financialImpact: type === 'detention' ? randBetween(200, 800) : type === 'damage' ? randBetween(500, 5000) : 0,
    assignedTo: pick(['user-001', 'user-002', 'user-003']),
    notes: '',
    ...uf({ loadId: load.id, customerId: load.customerId }, randBetween(1, 60)),
  };
});

// ═══════════════════════════════════════════════════════════════════
// SUMMARY
// ═══════════════════════════════════════════════════════════════════
export const SEED_SUMMARY = {
  customers: EXPANDED_CUSTOMERS.length,
  carriers: EXPANDED_CARRIERS.length,
  loads: EXPANDED_LOADS.length,
  quotes: EXPANDED_QUOTES.length,
  purchaseOrders: EXPANDED_PURCHASE_ORDERS.length,
  appointments: EXPANDED_APPOINTMENTS.length,
  carrierVetting: EXPANDED_CARRIER_VETTING.length,
  iotTrackers: EXPANDED_IOT_TRACKERS.length,
  iotEvents: EXPANDED_IOT_EVENTS.length,
  assets: EXPANDED_ASSETS.length,
  trucks: EXPANDED_TRUCKS.length,
  financials: EXPANDED_FINANCIALS.length,
  events: EXPANDED_EVENTS.length,
  exceptions: EXPANDED_EXCEPTIONS.length,
};
